/**
 * Drawing boxes on a whole dataset from a vocabulary the user typed.
 *
 * "kon si classes ke upar boxes banane hain" — the user names the objects, and
 * YOLO-World finds them. `/auto-detect/detect/custom` takes ONE image per
 * request, so this is the most expensive thing the assistant can do, and the
 * three properties below are what make it survivable.
 *
 * - **Bounded concurrency.** Fanning out 300 requests at once would queue 300
 *   GPU forward passes against a shared training API. A small window keeps the
 *   box responsive to other users and keeps cancellation meaningful.
 *
 * - **Every result is CACHED by image path, as it lands.** A run that is
 *   cancelled at image 280 of 300 has already paid for 280 forward passes;
 *   throwing them away because the 281st was interrupted is the difference
 *   between "stop and use what you have" and "start again". A retry resumes.
 *
 * - **The abort signal is checked before EVERY request**, not at step
 *   boundaries — otherwise Stop takes as long as the remaining queue.
 *
 * The conversion is the other half: the endpoint answers in ABSOLUTE PIXEL
 * xyxy, while everything downstream stores normalised centre form. Getting that
 * wrong does not raise — it draws every box in the wrong place.
 */

/** How many detections to have in flight at once. */
export const AUTO_BOX_CONCURRENCY = 3;

/** Below this share of images carrying a box, the vocabulary is probably wrong. */
export const LOW_COVERAGE_RATIO = 0.3;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

/**
 * Absolute pixel xyxy -> normalised centre form.
 *
 * Returns null for a box that cannot be expressed, rather than a zero-sized one
 * that would be silently dropped at training time.
 */
export const toNormalisedBox = (detection, imageSize, classIndexOf) => {
  const width = Number(imageSize && imageSize.width) || 0;
  const height = Number(imageSize && imageSize.height) || 0;
  if (!width || !height) return null;

  const box = detection && detection.bbox;
  if (!box) return null;
  const x1 = Number(box.x1);
  const y1 = Number(box.y1);
  const x2 = Number(box.x2);
  const y2 = Number(box.y2);
  if (![x1, y1, x2, y2].every(Number.isFinite)) return null;

  const boxWidth = Math.abs(x2 - x1);
  const boxHeight = Math.abs(y2 - y1);
  if (boxWidth <= 0 || boxHeight <= 0) return null;

  const classIndex = classIndexOf(detection.class);
  if (classIndex == null) return null;

  return {
    classIndex,
    centerX: clamp01((Math.min(x1, x2) + boxWidth / 2) / width),
    centerY: clamp01((Math.min(y1, y2) + boxHeight / 2) / height),
    width: clamp01(boxWidth / width),
    height: clamp01(boxHeight / height),
    // Kept so the review step can re-threshold with no further GPU cost.
    confidence: Number(detection.confidence) || 0,
  };
};

/** Coverage and per-class counts, recomputed from the cache for free. */
export const summariseBoxes = (results, classes, threshold = 0) => {
  const perClass = classes.map(() => 0);
  let imagesWithBoxes = 0;
  let boxes = 0;

  results.forEach((entry) => {
    const kept = (entry.boxes || []).filter((box) => box.confidence >= threshold);
    if (kept.length) imagesWithBoxes += 1;
    boxes += kept.length;
    kept.forEach((box) => {
      if (perClass[box.classIndex] != null) perClass[box.classIndex] += 1;
    });
  });

  const total = results.length || 1;
  return {
    images: results.length,
    imagesWithBoxes,
    boxes,
    perClass,
    coverage: imagesWithBoxes / total,
    // The outcome that reads as success and trains a detector that finds
    // nothing: mostly-empty labels, imported cleanly as a "labeled dataset".
    lowCoverage: imagesWithBoxes / total < LOW_COVERAGE_RATIO,
  };
};

const isCancellation = (error) =>
  !!error &&
  (error.cancelled || error.name === 'AbortError' || error.name === 'CanceledError' ||
    error.code === 'ERR_CANCELED' || error.message === 'canceled');

/**
 * Run the vocabulary over every image.
 *
 * `cache` is a Map from image path to its result and is READ AND WRITTEN here,
 * so a second call after a cancellation only pays for what is missing.
 */
export const autoBoxImages = async ({
  items,
  classes,
  api,
  readBlob,
  cache,
  signal,
  confidence = 0.15,
  onProgress,
  concurrency = AUTO_BOX_CONCURRENCY,
}) => {
  const names = classes.map((entry) => entry.name);
  const lower = names.map((name) => String(name).trim().toLowerCase());
  const classIndexOf = (raw) => {
    const index = lower.indexOf(String(raw || '').trim().toLowerCase());
    return index >= 0 ? index : null;
  };

  const store = cache || new Map();
  const pending = items.filter((item) => !store.has(item.path));
  let done = items.length - pending.length;
  let failed = 0;
  let cancelled = false;

  const report = () => onProgress && onProgress({ done, total: items.length, failed });
  report();

  let cursor = 0;
  const worker = async () => {
    for (;;) {
      // Checked before EVERY request, so Stop is immediate rather than
      // taking as long as the rest of the queue.
      if (cancelled || (signal && signal.aborted)) return;
      const index = cursor;
      cursor += 1;
      if (index >= pending.length) return;
      const item = pending[index];

      try {
        // eslint-disable-next-line no-await-in-loop
        const blob = await readBlob(item.path);
        // eslint-disable-next-line no-await-in-loop
        const result = await api.detectCustom(
          { blob, name: item.name, classes: names, confidence },
          { signal },
        );
        const boxes = (result.detections || [])
          .map((detection) => toNormalisedBox(detection, result.image_size, classIndexOf))
          .filter(Boolean);
        // Written as it lands — this is what makes a cancel resumable.
        store.set(item.path, { path: item.path, name: item.name, boxes, size: result.image_size });
      } catch (error) {
        if (isCancellation(error)) {
          cancelled = true;
          return;
        }
        // One image that will not detect is not a reason to lose the rest.
        failed += 1;
        store.set(item.path, { path: item.path, name: item.name, boxes: [], failed: true });
      }
      done += 1;
      report();
    }
  };

  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(concurrency, pending.length || 1)) }, worker),
  );

  const results = items.map(
    (item) => store.get(item.path) || { path: item.path, name: item.name, boxes: [] },
  );
  return {
    results,
    cache: store,
    cancelled: cancelled || !!(signal && signal.aborted),
    failed,
    // A cancelled run still hands back everything it paid for.
    completed: items.length - items.filter((item) => !store.has(item.path)).length,
  };
};
